import React, { useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import './LoadingScreen.css';

// Preload BagBuilder component
let BagBuilderPreloaded = null;
let BagBuilderPreloadPromise = null;
const preloadBagBuilder = () => {
  if (!BagBuilderPreloadPromise) {
    BagBuilderPreloadPromise = import('../BagBuilder/BagBuilder').then(module => {
      BagBuilderPreloaded = module.default;
      return module.default;
    });
  }
  return BagBuilderPreloadPromise;
};

const LoadingScreen = ({ onComplete }) => {
  const { t } = useTranslation();
  const [isVisible, setIsVisible] = useState(false);
  const [progress, setProgress] = useState(0);
  const [isHiding, setIsHiding] = useState(false);
  const bagBuilderPromiseRef = useRef(null);
  const onCompleteRef = useRef(onComplete);
  const hasStartedAnimationRef = useRef(false); // Flag to prevent restart

  // Update ref when onComplete changes
  useEffect(() => {
    onCompleteRef.current = onComplete;
  }, [onComplete]);

  useEffect(() => {
    // Prevent restart if animation already started
    if (hasStartedAnimationRef.current) {
      return;
    }
    
    let animationFrameId = null;
    let progressTimer = null;
    let showTimer = null;
    let hasStartedPreload = false; // Flag to track if preload has started
    
    // Mark animation as started
    hasStartedAnimationRef.current = true;
    
    // Wait for screen to appear before starting animation
    showTimer = setTimeout(() => {
      setIsVisible(true);
      
      // Start progress animation after screen is visible
      progressTimer = setTimeout(() => {
        const duration = 2000; // 2 seconds maximum
        const startTime = Date.now();
        
        const updateProgress = () => {
          const now = Date.now();
          const elapsed = now - startTime;
          const percent = Math.min(100, (elapsed / duration) * 100);
          
          // Update progress state
          setProgress(percent);
          // console.log('Progress:', percent);
          
          // Start preloading BagBuilder when progress reaches 40%
          if (!hasStartedPreload && percent >= 40) {
            hasStartedPreload = true;
            console.log('Starting BagBuilder preload at 40%');
            bagBuilderPromiseRef.current = preloadBagBuilder();
          }
          
          // Continue animation if not complete
          if (percent < 100) {
            animationFrameId = requestAnimationFrame(updateProgress);
          } else {
            // Animation complete - wait for BagBuilder to be loaded before completing
            setProgress(100);
            console.log('Progress complete, waiting for BagBuilder...');
            
            // Wait for BagBuilder to load, then hide and complete
            if (bagBuilderPromiseRef.current) {
              bagBuilderPromiseRef.current.then(() => {
                console.log('BagBuilder loaded, completing...');
                // Small delay before hiding
                setTimeout(() => {
                  // Start hiding animation
                  setIsHiding(true);
                  // Wait for animation to complete before calling onComplete
                  setTimeout(() => {
                    if (onCompleteRef.current) {
                      onCompleteRef.current();
                    }
                  }, 600); // Match the transition duration
                }, 200);
              }).catch(err => {
                console.error('Error loading BagBuilder:', err);
                // Continue anyway after a delay
                setTimeout(() => {
                  setIsHiding(true);
                  setTimeout(() => {
                    if (onCompleteRef.current) {
                      onCompleteRef.current();
                    }
                  }, 600);
                }, 200);
              });
            } else {
              // If BagBuilder wasn't loaded yet, start loading now and wait
              console.log('BagBuilder not started, loading now...');
              bagBuilderPromiseRef.current = preloadBagBuilder();
              bagBuilderPromiseRef.current.then(() => {
                console.log('BagBuilder loaded, completing...');
                setTimeout(() => {
                  setIsHiding(true);
                  setTimeout(() => {
                    if (onCompleteRef.current) {
                      onCompleteRef.current();
                    }
                  }, 600);
                }, 200);
              }).catch(err => {
                console.error('Error loading BagBuilder:', err);
                setTimeout(() => {
                  setIsHiding(true);
                  setTimeout(() => {
                    if (onCompleteRef.current) {
                      onCompleteRef.current();
                    }
                  }, 600);
                }, 200);
              });
            }
          }
        };
        
        // Start the animation immediately - set initial value and start loop
        setProgress(0);
        animationFrameId = requestAnimationFrame(updateProgress);
      }, 200);
    }, 50);
    
    return () => {
      hasStartedAnimationRef.current = false; // Reset flag on cleanup
      if (showTimer) clearTimeout(showTimer);
      if (progressTimer) clearTimeout(progressTimer);
      if (animationFrameId) cancelAnimationFrame(animationFrameId);
    };
  }, []); // Empty dependencies - only run once on mount

  return (
    <div className={`loading-screen ${isVisible && !isHiding ? 'loading-screen--visible' : ''}`}>
      <div className="loading-screen__content">
        <h2 className="loading-screen__title">
          {t('LOADING_SCREEN_TITLE')}
        </h2>
        <p className="loading-screen__description">
          {t('LOADING_SCREEN_DESCRIPTION')}
        </p>
        <div className="loading-screen__progress">
          <div 
            className="loading-screen__progress-bar" 
            style={{ width: `${progress}%` }}
          ></div>
        </div>
        {/* Debug: Remove this later 
        <div style={{ marginTop: '10px', fontSize: '12px', color: '#666' }}>
          Progress: {progress.toFixed(1)}%
        </div>
        */}
      </div>
    </div>
  );
};

export default LoadingScreen;

